import { getDataType } from '../../../../../graph/data-types';
import {
    buildStaticTableShapeFromColumns,
    resolveColumnLabel,
} from '../../runtime/dataServices/tabular';
import type { RenderContext, RenderContextKind } from '../types';
import type {
    HorizontalAlignment,
    TableBinding,
    TableLayoutShape,
    TableSettings,
} from './contracts';
import {
    getBindingCatalog,
    createBindingId,
    type RuntimeBindingDescriptor,
} from '../../runtime/dataServices/bindingCatalog';
import type { RuntimeDataServiceId } from '../../runtime/dataServices/contracts';
import type {
    BindingId,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularCellRenderOptions,
    RuntimeTabularColumnDescriptor,
} from '../../runtime/dataServices/types';

export type TableRuntimeBindingDescriptor = RuntimeBindingDescriptor<
    RuntimeResultLinkDescriptorTabular<RuntimeDataServiceId, unknown>
>;

function assertTableDefaultsOrThrow(
    columns: readonly RuntimeTabularColumnDescriptor[],
    bindingId: BindingId
): {
    defaultHeader: string[];
    defaultColumnAlignments: HorizontalAlignment[];
} {
    if (!columns.length) {
        throw new Error(`Missing "${bindingId}" tabular column metadata.`);
    }

    const resolvedHeader = columns.map((column) =>
        column.kind === 'measurement'
            ? column.label ?? getDataType(column.dataType).title
            : column.label
    );
    const resolvedAlignments = columns.map((column) => column.defaultAlignment ?? 'left');

    return {
        defaultHeader: resolvedHeader,
        defaultColumnAlignments: resolvedAlignments,
    };
}

function assertTableRuntimeBindingDescriptorOrThrow(
    descriptor: RuntimeBindingDescriptor
): TableRuntimeBindingDescriptor {
    if (descriptor.link.valueKind !== 'tabular') {
        throw new Error(
            `Runtime binding "${descriptor.bindingId}" does not resolve to tabular link.`
        );
    }

    return descriptor as TableRuntimeBindingDescriptor;
}

export function listTableRuntimeBindingsForContext(
    contextKind: RenderContextKind
): readonly TableRuntimeBindingDescriptor[] {
    return getBindingCatalog().listForContextAndValueKind(
        contextKind,
        'tabular'
    ) as readonly TableRuntimeBindingDescriptor[];
}

export function getTableRuntimeBindingDescriptorByIdOrThrow(
    bindingId: BindingId
): TableRuntimeBindingDescriptor {
    return assertTableRuntimeBindingDescriptorOrThrow(
        getBindingCatalog().getByIdAndValueKindOrThrow(bindingId, 'tabular')
    );
}

export function getTableRuntimeBindingDescriptorOrThrow(
    binding: TableBinding
): TableRuntimeBindingDescriptor {
    return getTableRuntimeBindingDescriptorByIdOrThrow(createBindingId(binding));
}

export function createDefaultTableLayoutFromBinding(
    binding: TableBinding
): Pick<
    TableSettings,
    'title' | 'header' | 'rows' | 'columnAlignments' | 'binding' | 'showWhenEmpty'
> {
    const descriptor = getTableRuntimeBindingDescriptorOrThrow(binding);
    const { defaultHeader, defaultColumnAlignments } = assertTableDefaultsOrThrow(
        descriptor.link.columns,
        descriptor.bindingId
    );

    return {
        showWhenEmpty: false,
        title: descriptor.link.label,
        header: defaultHeader,
        rows: [],
        columnAlignments: defaultColumnAlignments,
        binding: getBindingCatalog().toActiveDataBinding(descriptor, binding.arg),
    };
}

/**
 * The render contract for a tabular link: resolve every header, then let the link build its own
 * shape or fall back to the column-driven one. The element and the tests share it, so a test
 * cannot assert a header the element would not render.
 */
export function buildLinkTableShape<TEntry>(
    link: {
        columns: readonly RuntimeTabularColumnDescriptor<TEntry>[];
        parseTableRows: (value: unknown) => readonly TEntry[];
        buildTableShape?: RuntimeResultLinkDescriptorTabular['buildTableShape'];
    },
    linkValue: unknown,
    settings: TableLayoutShape,
    context: RenderContext,
    options: RuntimeTabularCellRenderOptions
): TableLayoutShape {
    const resolvedSettings = {
        ...settings,
        // Resolved per render rather than taken from the settings, because a measurement
        // column's label depends on the profile's unit and the measuring point's naming
        // convention. Alignments need no such resolution, and `normalizeTableSettings` already
        // re-derives them from the binding's columns, so they are passed through.
        header: link.columns.map((column) => resolveColumnLabel(column, options)),
    };

    if (link.buildTableShape) {
        return link.buildTableShape(resolvedSettings, linkValue, context, options);
    }

    return buildStaticTableShapeFromColumns(
        resolvedSettings,
        linkValue,
        link.columns,
        link.parseTableRows,
        options
    );
}
